import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useForm,
    useAuth,
    useFetch,
    useResource,
    useGrid,
    useModal,
    useStore,
    useToast,
    usePopup,
    usePage,
} from "@/comn/hooks"; // hook
import {
    BASE,
    URLS,
    APIS,
    SF_BTCH_INFO_FORM,
    SG_BTCH_INFO_SCHD_LIST,
    SG_BTCH_INFO_PRMT_LIST,
} from "@/tz/go/egaz/osci/ptl/btch/info/services/OsciBtchInfoMgmtService";

/*
 * Manage batch information details
 * !== 배치정보관리 상세 ==!
 */
export const OSCI0112004Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { openPopup } = usePopup();
    const { btchId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [{ area: "comnCd", comnCd: "OSC0088" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Manage batch information Form !== 배치정보관리 Form ==!
        btchInfoForm: useForm({
            defaultSchema: SF_BTCH_INFO_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        btchInfoSchdList: useGrid({
            defaultSchema: SG_BTCH_INFO_SCHD_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        btchInfoPrmtList: useGrid({
            defaultSchema: SG_BTCH_INFO_PRMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Batch Information !== 배치 정보 조회 ==!
        getBtchInfo: useFetch({
            api: () => APIS.getBtchInfo(btchId),
            enabled: !!btchId,
            onSuccess: (data) => {
                const btchInfo = data.btchInfo.content;
                const btchPrmtList = btchInfo.btchPrmtList;
                const schdList = btchInfo.schdList;

                btchInfo.onlnExcutnPsblYn = btchInfo.onlnExcutnPsblYn === "Y" ? "YES" : "NO";

                form.btchInfoForm.setValues(btchInfo);

                // Schedule list settings !== 스케줄 목록 세팅 ==!
                schdList.forEach((item: any) => {
                    grid.btchInfoSchdList.addRow({ ...item });
                });

                // Setting batch parameter list !== 배치 파라미터 목록 세팅 ==!
                btchPrmtList.forEach((item: any) => {
                    grid.btchInfoPrmtList.addRow({ ...item });
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate(URLS.osci0112001q);
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
            },
        }),
        // delete Batch !== 배치 삭제 ==!
        deleteBtchInfo: useFetch({
            api: () => APIS.deleteBtchInfo(btchId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00071", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onCancel: () => {
                        navigate(URLS.osci0112001q);
                    },
                });
            },
            onError: () => {},
            showToast: true,
        }),
        // Pause Batch !== 배치 중지 ==!
        pauseBtch: useFetch({
            api: (data) => APIS.pauseBtch({ ...data }),
            onSuccess: (data) => {
                // ExitCode definition = A: FAIL, B: SUCCESS
                const exitCode = data.exitInfo.content.exitCode;
                const content = exitCode === "A" ? "msg.osc.00121" : "msg.osc.00119";

                modal.openModal({
                    content: content,
                    onConfirm: () => {},
                    close: false,
                });
            },
            onError: (error: any) => {
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
        // Resume Batch !== 배치 재실행 ==!
        resumeBtch: useFetch({
            api: (data) => APIS.resumeBtch({ ...data }),
            onSuccess: (data) => {
                // ExitCode definition = A: FAIL, B: SUCCESS
                const exitCode = data.exitInfo.content.exitCode;
                const content = exitCode === "A" ? "msg.osc.00122" : "msg.osc.00112";

                modal.openModal({
                    content: content,
                    onConfirm: () => {},
                    close: false,
                });
            },
            onError: (error: any) => {
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    toast.showToast({ type: "error", content: "msg.osc.00035" }); // An error occurred. !== 에러가 발생했습니다! ==!
                }
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete Batch !== 배치 삭제 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.osc.00062", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    fetch.deleteBtchInfo.fetch();
                },
            });
        },
        onEditBtnClick: () => {
            navigate(`${URLS.osci0112003s}/${btchId}`);
        },
        // on Stop Button Click Event Handler  !== 배치 중지 ==!
        onStopBtnClick: () => {
            if (!comnUtils.isEmpty(form.btchInfoForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.btchInfoForm.handleSubmit(
                () => {
                    const data = form.btchInfoForm.getValues();

                    modal.openModal({
                        content: "msg.osc.00118", // Do you want to stop? !== 중지 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.pauseBtch.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // on rerun Button Click Event Handler  !== 배치 재실행 ==!
        onRerunBtnClick: () => {
            if (!comnUtils.isEmpty(form.btchInfoForm.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.btchInfoForm.handleSubmit(
                () => {
                    const data = form.btchInfoForm.getValues();

                    modal.openModal({
                        content: "msg.osc.00180", // Do you want to execute? !== 실행 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.resumeBtch.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    /*
     * Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        form.btchInfoForm.setEditable(false);

        // Setting batch parameters grid query version !== 배치 파라미터 그리드 조회 버전으로 세팅 ==!
        grid.btchInfoPrmtList.setOption("edit", false);
        grid.btchInfoPrmtList.setOption("checkbox", false);
        grid.btchInfoPrmtList.setOption("index", true);

        fetch.getBtchInfo.fetch();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.btchId} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.btchNm} controlSize={10} required={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.btchDesc} controlSize={10} required={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchInfoForm.schema.bsopClsfCd} controlSize={10} required={false} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.btchInfoForm.schema.onlnExcutnPsblYn}
                                controlSize={10}
                                required={false}
                                type="text"
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.btchInfoForm.schema.frstRgsrDtm}
                                controlSize={10}
                                required={false}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.btchInfoForm.schema.lastMdfyDtm}
                                controlSize={10}
                                required={false}
                            />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_BTCH_SCHD_LIST"} titleSize={2}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid {...grid.btchInfoSchdList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_BTCH_PRMT_LIST"} titleSize={2}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid {...grid.btchInfoPrmtList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0112001q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Button
                        variant="warning"
                        onClick={() => {
                            handler.onStopBtnClick();
                        }}
                    >
                        {t("B_SCHD_SSPS")}
                    </Button>
                    <Button
                        variant="warning"
                        onClick={() => {
                            handler.onRerunBtnClick();
                        }}
                    >
                        {t("B_SCHD_RE_RUN")}
                    </Button>
                    <Layout.Button
                        role={"edit"}
                        onClick={() => {
                            handler.onEditBtnClick();
                        }}
                    />
                    <Layout.Button
                        role={"delete"}
                        onClick={() => {
                            handler.onDeleteBtnClick();
                        }}
                    />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
